/* Static UI-chrome strings for the customer ordering flow (menu, product
   detail, cart, order success). Product data itself (names/descriptions/
   categories) is translated server-side — see base/utils/translation.py.

   Scalable to more languages: add one more top-level object here and wire
   its flag/label into LANGUAGES below (backend SUPPORTED_LANGUAGES in
   base/utils/translation.py must also list the new code). */

export const LANGUAGES = [
  { code: "hu", label: "Magyar", flag: "🇭🇺" },
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "de", label: "Deutsch", flag: "🇩🇪" },
];

/* Product descriptions carry a free-text allergen note that machine
   translation renders in whichever language it was translated to — try
   every known variant of the label so the split works regardless of the
   active language. */
export const ALLERGEN_LABELS = ["Allergének:", "Allergens:", "Allergene:"];

const translations = {
  hu: {
    popularCategoryLabel: "Népszerű",
    searchPlaceholder: "Keresés az étlapon...",
    searchPrefix: "Keresés",
    noProductsFoundTitle: "Nincs találat",
    noProductsFoundMessage: "Próbálj más kulcsszót, vagy nézd meg az összes kategóriát.",
    emptyMenuTitle: "A menü jelenleg üres",
    emptyMenuMessage: "Ez az étterem még nem töltött fel elérhető ételeket.",
    inCartBadge: "A kosaradban",
    viewCart: "Kosár megtekintése",
    yourCart: "Kosarad",
    cartTitle: "Kosár",
    emptyCartTitle: "A kosarad üres",
    emptyCartMessage: "Böngéssz az étlapon, és add hozzá a kedvenc ételeidet a kosárhoz.",
    backToMenu: "Vissza az étlaphoz",
    chooseTable: "Válassz asztalt",
    chooseTablePlaceholder: "-- Válassz asztalt --",
    chooseTableError: "Kérlek válassz asztalt a rendelés véglegesítéséhez!",
    orderNote: "Megjegyzés a rendeléshez",
    orderNotePlaceholder: "Pl: Allergia, extra kérés",
    charactersCount: (n) => `${n}/250 karakter`,
    placeOrder: "Rendelés véglegesítése",
    productsCount: (n) => `Termékek (${n} db)`,
    subtotal: "Részösszeg",
    total: "Összesen",
    addToCart: "Kosárba",
    required: "Kötelező",
    selectAtLeastOne: "Kérlek válassz legalább egy opciót!",
    noProductData: "Nincs adat a termékről.",
    backAria: "Vissza az étlaphoz",
    closeAria: "Bezárás",
    orderSuccessTitle: "Sikeres rendelés!",
    orderNumberLabel: "Rendelés száma:",
    language: "Nyelv",
    trackingTitle: "Rendelésed állapota",
    trackingSubtitle: "Ezen az oldalon nyomon követheted, hol tart a rendelésed.",
    orderStatusReceived: "Beérkezett",
    orderStatusPreparing: "Készül",
    orderStatusReady: "Elkészült",
    orderStatusCompleted: "Átadva",
    removeFromCart: "Eltávolítás a kosárból",
    openNow: "Nyitva",
    closedNow: "Zárva",
    openingHoursTitle: "Nyitvatartás",
    weekdays: ["Hétfő", "Kedd", "Szerda", "Csütörtök", "Péntek", "Szombat", "Vasárnap"],
    closedOrderingMessage: "Az étterem jelenleg zárva tart, rendelés most nem lehetséges.",

    upsellPatternTitleDetail: "Ezt is szokták rendelni hozzá",
    upsellFallbackTitleDetail: "Ezt is ajánljuk",
    upsellPatternTitleCart: "Ezt is szokták még rendelni",
    upsellFallbackTitleCart: "Ezt is ajánljuk",
    upsellDismissAria: "Ajánlás elrejtése",

    reservationsNavLink: "Foglalás",
    eventsNavLink: "Események",
    eventsPageTitle: "Események",
    upcomingEventsTitle: "Közelgő események",
    emptyEventsTitle: "Jelenleg nincs esemény",
    emptyEventsMessage: "Nézz vissza később — hamarosan új eseményeket hirdetünk.",
    eventSoldOut: "Betelt",
    eventFreeLabel: "Ingyenes",
    eventPriceSuffix: "Ft/fő",
    eventCapacityLabel: (booked, max) =>
      max != null ? `${booked} / ${max} fő foglalt` : `${booked} fő foglalt`,
    eventBookButton: "Helyfoglalás",
    backToEvents: "Vissza az eseményekhez",
    reservationPageTitle: "Asztalfoglalás",
    partySizeLabel: "Hány főre?",
    dateLabel: "Melyik napra?",
    timeLabel: "Melyik időpontra?",
    noSlotsAvailable: "Erre a napra sajnos nincs szabad időpont ekkora létszámra.",
    pickDateFirst: "Válassz dátumot a szabad időpontok megtekintéséhez.",
    guestNameLabel: "Teljes név",
    guestEmailLabel: "Email cím",
    guestPhoneLabel: "Telefonszám",
    guestNoteLabel: "Megjegyzés (opcionális)",
    guestNotePlaceholder: "Pl: ablak melletti asztalt kérnék",
    confirmReservationButton: "Foglalás véglegesítése",
    reservationSuccessTitle: "Foglalásod rögzítettük!",
    reservationPendingTitle: "Foglalásodat rögzítettük, visszaigazolásra vár",
    reservationConfirmedNote: "Hamarosan email visszaigazolást küldünk a megadott címre.",
    reservationPendingNote: "Az étterem hamarosan visszaigazolja a foglalásodat emailben.",
    reservationDetailsLabel: "Foglalás adatai",
    reservationDateTimeLabel: "Időpont",
    reservationPartySizeLabel: "Létszám",
    manageReservationLink: "Foglalás megtekintése / lemondása",
    reservationLookupTitle: "Foglalásod adatai",
    reservationNotFound: "Ez a foglalás nem található.",
    reservationStatusLabel: "Állapot",
    cancelReservationButton: "Foglalás lemondása",
    cancelReservationConfirm: "Biztosan lemondod a foglalást?",
    reservationCancelledMessage: "A foglalás lemondva.",
    reservationStatusPending: "Függőben",
    reservationStatusConfirmed: "Visszaigazolva",
    reservationStatusSeated: "Leültetve",
    reservationStatusCompleted: "Lezárva",
    reservationStatusCancelled: "Lemondva",
    reservationStatusNoShow: "Nem jelent meg",
    submitting: "Küldés...",
  },
  en: {
    popularCategoryLabel: "Popular",
    searchPlaceholder: "Search the menu...",
    searchPrefix: "Search",
    noProductsFoundTitle: "No products found",
    noProductsFoundMessage: "Try a different search term, or browse all categories.",
    emptyMenuTitle: "The menu is currently empty",
    emptyMenuMessage: "This restaurant hasn't added any available items yet.",
    inCartBadge: "In your cart",
    viewCart: "View cart",
    yourCart: "Your cart",
    cartTitle: "Cart",
    emptyCartTitle: "Your cart is empty",
    emptyCartMessage: "Browse the menu and add your favorite dishes to the cart.",
    backToMenu: "Back to menu",
    chooseTable: "Choose a table",
    chooseTablePlaceholder: "-- Choose a table --",
    chooseTableError: "Please choose a table to place your order!",
    orderNote: "Note for your order",
    orderNotePlaceholder: "E.g.: Allergies, special request",
    charactersCount: (n) => `${n}/250 characters`,
    placeOrder: "Place order",
    productsCount: (n) => `Items (${n})`,
    subtotal: "Subtotal",
    total: "Total",
    addToCart: "Add to cart",
    required: "Required",
    selectAtLeastOne: "Please select at least one option!",
    noProductData: "No data available for this product.",
    backAria: "Back to menu",
    closeAria: "Close",
    orderSuccessTitle: "Order successful!",
    orderNumberLabel: "Order number:",
    language: "Language",
    trackingTitle: "Your order status",
    trackingSubtitle: "Track the progress of your order right here.",
    orderStatusReceived: "Received",
    orderStatusPreparing: "Preparing",
    orderStatusReady: "Ready",
    orderStatusCompleted: "Completed",
    removeFromCart: "Remove from cart",
    openNow: "Open now",
    closedNow: "Closed",
    openingHoursTitle: "Opening hours",
    weekdays: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    closedOrderingMessage: "This restaurant is currently closed — ordering isn't available right now.",

    upsellPatternTitleDetail: "Goes well with this",
    upsellFallbackTitleDetail: "You might also like",
    upsellPatternTitleCart: "You might also like",
    upsellFallbackTitleCart: "You might also like",
    upsellDismissAria: "Dismiss suggestion",

    reservationsNavLink: "Reserve",
    eventsNavLink: "Events",
    eventsPageTitle: "Events",
    upcomingEventsTitle: "Upcoming events",
    emptyEventsTitle: "No events right now",
    emptyEventsMessage: "Check back later — new events are announced regularly.",
    eventSoldOut: "Sold out",
    eventFreeLabel: "Free",
    eventPriceSuffix: "/ guest",
    eventCapacityLabel: (booked, max) =>
      max != null ? `${booked} / ${max} booked` : `${booked} booked`,
    eventBookButton: "Book a spot",
    backToEvents: "Back to events",
    reservationPageTitle: "Table reservation",
    partySizeLabel: "How many guests?",
    dateLabel: "Which day?",
    timeLabel: "Which time?",
    noSlotsAvailable: "Sorry, no availability for this day at this party size.",
    pickDateFirst: "Pick a date to see available times.",
    guestNameLabel: "Full name",
    guestEmailLabel: "Email address",
    guestPhoneLabel: "Phone number",
    guestNoteLabel: "Note (optional)",
    guestNotePlaceholder: "E.g.: window seat if possible",
    confirmReservationButton: "Confirm reservation",
    reservationSuccessTitle: "Your reservation is booked!",
    reservationPendingTitle: "Your reservation is in, awaiting confirmation",
    reservationConfirmedNote: "You'll get a confirmation email shortly.",
    reservationPendingNote: "The restaurant will confirm your reservation by email shortly.",
    reservationDetailsLabel: "Reservation details",
    reservationDateTimeLabel: "When",
    reservationPartySizeLabel: "Guests",
    manageReservationLink: "View / cancel reservation",
    reservationLookupTitle: "Your reservation",
    reservationNotFound: "This reservation could not be found.",
    reservationStatusLabel: "Status",
    cancelReservationButton: "Cancel reservation",
    cancelReservationConfirm: "Are you sure you want to cancel this reservation?",
    reservationCancelledMessage: "Reservation cancelled.",
    reservationStatusPending: "Pending",
    reservationStatusConfirmed: "Confirmed",
    reservationStatusSeated: "Seated",
    reservationStatusCompleted: "Completed",
    reservationStatusCancelled: "Cancelled",
    reservationStatusNoShow: "No-show",
    submitting: "Sending...",
  },
  de: {
    popularCategoryLabel: "Beliebt",
    searchPlaceholder: "Speisekarte durchsuchen...",
    searchPrefix: "Suche",
    noProductsFoundTitle: "Keine Produkte gefunden",
    noProductsFoundMessage: "Versuche einen anderen Suchbegriff oder durchstöbere alle Kategorien.",
    emptyMenuTitle: "Die Speisekarte ist derzeit leer",
    emptyMenuMessage: "Dieses Restaurant hat noch keine verfügbaren Gerichte hinzugefügt.",
    inCartBadge: "Im Warenkorb",
    viewCart: "Warenkorb ansehen",
    yourCart: "Dein Warenkorb",
    cartTitle: "Warenkorb",
    emptyCartTitle: "Dein Warenkorb ist leer",
    emptyCartMessage: "Durchstöbere die Speisekarte und füge deine Lieblingsgerichte hinzu.",
    backToMenu: "Zurück zur Speisekarte",
    chooseTable: "Tisch auswählen",
    chooseTablePlaceholder: "-- Tisch auswählen --",
    chooseTableError: "Bitte wähle einen Tisch, um die Bestellung abzuschließen!",
    orderNote: "Anmerkung zur Bestellung",
    orderNotePlaceholder: "Z.B.: Allergien, Extrawunsch",
    charactersCount: (n) => `${n}/250 Zeichen`,
    placeOrder: "Bestellung abschließen",
    productsCount: (n) => `Artikel (${n})`,
    subtotal: "Zwischensumme",
    total: "Gesamt",
    addToCart: "In den Warenkorb",
    required: "Erforderlich",
    selectAtLeastOne: "Bitte wähle mindestens eine Option!",
    noProductData: "Keine Daten zu diesem Produkt verfügbar.",
    backAria: "Zurück zur Speisekarte",
    closeAria: "Schließen",
    orderSuccessTitle: "Bestellung erfolgreich!",
    orderNumberLabel: "Bestellnummer:",
    language: "Sprache",
    trackingTitle: "Status deiner Bestellung",
    trackingSubtitle: "Verfolge hier den Fortschritt deiner Bestellung.",
    orderStatusReceived: "Eingegangen",
    orderStatusPreparing: "Wird zubereitet",
    orderStatusReady: "Fertig",
    orderStatusCompleted: "Abgeschlossen",
    removeFromCart: "Aus dem Warenkorb entfernen",
    openNow: "Geöffnet",
    closedNow: "Geschlossen",
    openingHoursTitle: "Öffnungszeiten",
    weekdays: ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"],
    closedOrderingMessage: "Dieses Restaurant hat derzeit geschlossen — Bestellungen sind gerade nicht möglich.",

    upsellPatternTitleDetail: "Passt gut dazu",
    upsellFallbackTitleDetail: "Das könnte dir auch gefallen",
    upsellPatternTitleCart: "Das bestellen andere oft dazu",
    upsellFallbackTitleCart: "Das könnte dir auch gefallen",
    upsellDismissAria: "Empfehlung ausblenden",

    reservationsNavLink: "Reservieren",
    eventsNavLink: "Veranstaltungen",
    eventsPageTitle: "Veranstaltungen",
    upcomingEventsTitle: "Kommende Veranstaltungen",
    emptyEventsTitle: "Derzeit keine Veranstaltungen",
    emptyEventsMessage: "Schau später wieder vorbei — neue Events folgen bald.",
    eventSoldOut: "Ausgebucht",
    eventFreeLabel: "Kostenlos",
    eventPriceSuffix: "/ Person",
    eventCapacityLabel: (booked, max) =>
      max != null ? `${booked} / ${max} belegt` : `${booked} belegt`,
    eventBookButton: "Platz reservieren",
    backToEvents: "Zurück zu den Veranstaltungen",
    reservationPageTitle: "Tischreservierung",
    partySizeLabel: "Wie viele Personen?",
    dateLabel: "An welchem Tag?",
    timeLabel: "Zu welcher Uhrzeit?",
    noSlotsAvailable: "Für diesen Tag und diese Personenzahl ist leider nichts frei.",
    pickDateFirst: "Wähle ein Datum, um freie Zeiten zu sehen.",
    guestNameLabel: "Vollständiger Name",
    guestEmailLabel: "E-Mail-Adresse",
    guestPhoneLabel: "Telefonnummer",
    guestNoteLabel: "Anmerkung (optional)",
    guestNotePlaceholder: "Z.B.: Tisch am Fenster, wenn möglich",
    confirmReservationButton: "Reservierung bestätigen",
    reservationSuccessTitle: "Deine Reservierung ist bestätigt!",
    reservationPendingTitle: "Deine Reservierung wurde eingereicht und wartet auf Bestätigung",
    reservationConfirmedNote: "Du erhältst in Kürze eine Bestätigungs-E-Mail.",
    reservationPendingNote: "Das Restaurant bestätigt deine Reservierung in Kürze per E-Mail.",
    reservationDetailsLabel: "Reservierungsdetails",
    reservationDateTimeLabel: "Zeitpunkt",
    reservationPartySizeLabel: "Personen",
    manageReservationLink: "Reservierung ansehen / stornieren",
    reservationLookupTitle: "Deine Reservierung",
    reservationNotFound: "Diese Reservierung wurde nicht gefunden.",
    reservationStatusLabel: "Status",
    cancelReservationButton: "Reservierung stornieren",
    cancelReservationConfirm: "Möchtest du diese Reservierung wirklich stornieren?",
    reservationCancelledMessage: "Reservierung storniert.",
    reservationStatusPending: "Ausstehend",
    reservationStatusConfirmed: "Bestätigt",
    reservationStatusSeated: "Platziert",
    reservationStatusCompleted: "Abgeschlossen",
    reservationStatusCancelled: "Storniert",
    reservationStatusNoShow: "Nicht erschienen",
    submitting: "Wird gesendet...",
  },
};

export const t = (key, language) => {
  const dict = translations[language] || translations.hu;
  return dict[key] ?? translations.hu[key] ?? key;
};

export default translations;
